import { useMemo } from 'react';

import { Loading, LoadingError, type RequestError, ResourceNotFound } from '../../shared';
import { AccountOwnerPanel } from '../components/AccountOwnerPanel';
import { useMembers } from '../hooks/useMembers';
import { useUpdateAccountOwner } from '../hooks/useUpdateAccountOwner';
import { type Account, type Member, type OwnerPromotion } from '../model';

type OwnProps = Readonly<{
  account: Account;
}>;

export default function AccountOwnerPanelContainer({ account }: OwnProps) {
  // Default (paginated=false) list to get full membership for selection
  const membersQuery = useMembers(account, { paginated: false, enabled: Boolean(account) });

  // Owning the mutation here removes the effect that edge-detected a Redux
  // isFetching true -> false transition to invalidate the members cache; the
  // mutation's own onSuccess does that from a real success signal.
  const updateOwner = useUpdateAccountOwner(account);

  const isFetching = Boolean(membersQuery.isLoading);
  const error: RequestError | null = membersQuery.error || null;

  const members: Member[] = useMemo(() => {
    const entitiesMembers = membersQuery.data?.entities?.members;
    const list: Member[] = entitiesMembers ? (Object.values(entitiesMembers) as Member[]) : [];
    return list.slice().sort((a, b) => a.email.localeCompare(b.email));
  }, [membersQuery.data]);

  if (isFetching) {
    return <Loading title="Loading account owner" />;
  }

  if (error) {
    const { status } = error;
    switch (status) {
      case 404:
        return (
          <ResourceNotFound
            title="Account owner not found"
            description={<p>Account owner not found</p>}
            returnTo="/accounts"
            returnLabel="Go back to accounts"
          />
        );
      default:
        return <LoadingError title="Failed to account owner" />;
    }
  }

  return (
    <AccountOwnerPanel
      account={account}
      members={members}
      isUpdatingAccountOwner={updateOwner.isPending}
      accountOwnerUpdateError={updateOwner.error ?? null}
      onChangeAccountOwner={(promotion: OwnerPromotion) => updateOwner.mutate(promotion)}
    />
  );
}
